import { effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { ActiveUserService } from './active-user.service';
import { AuthService } from './auth.service';
import { shouldEnterLegalGate } from './auth-navigation';
import { GlLegalAbsentTemplateService } from './gl-legal-absent-template.service';
import { TripService } from './trip.service';

/**
 * Re-evaluates the GL legal gate after the trip payload loads and on trip switch.
 * `tripAgreementsGuard` must allow unloaded trips, so it cannot cover those cases alone.
 */
@Injectable({ providedIn: 'root' })
export class GlLegalGateRedirectService {
  private readonly router = inject(Router);
  private readonly tripService = inject(TripService);
  private readonly activeUserService = inject(ActiveUserService);
  private readonly authService = inject(AuthService);
  private readonly absentTemplateService = inject(GlLegalAbsentTemplateService);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  constructor() {
    effect(() => {
      const url = this.currentUrl();
      const authUserTrip = this.tripService.authUserTrip;
      const trip = authUserTrip.hasValue() ? authUserTrip.value() : undefined;
      const tripId = this.tripService.currentTripId();

      if (
        !shouldEnterLegalGate(url, trip, {
          isActingAsOther: this.activeUserService.isActingAsOther(),
          isStaffImpersonation: this.authService.isStaffImpersonation(),
          tripLoaded: this.tripService.currentTripLoaded(),
          absentTemplateKeys: this.absentTemplateService.absentKeysForTrip(tripId),
          hasEnded: this.tripService.hasEnded(),
        })
      ) {
        return;
      }

      void this.router.navigate(['/trip-agreements'], {
        queryParams: { returnUrl: url },
      });
    });
  }
}
